import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { jobsApi } from '../api/client'
import Button from '../components/ui/Button'
import DropZone from '../components/ui/DropZone'

/**
 * A7/A8: upload your own card images (fronts + optional custom backs) instead
 * of resolving against Scryfall — the web port of the desktop "Locals" tab.
 *
 * - Fronts: one image per card (order preserved).
 * - Backs: numbered custom backs; each front can be assigned one (or none —
 *   the back cell is left blank, keeping duplex alignment).
 * - Crop: apply the MPC bleed crop to every image, or per front.
 * - Multiselection (T-03): select several fronts and assign the same back,
 *   toggle crop or remove them in one batch operation — parity with the
 *   desktop GUI's drag-select multiselect (gui/locals_tab.py).
 */
export default function LocalUpload() {
  const navigate = useNavigate()
  const [fronts, setFronts] = useState<File[]>([])
  const [backs, setBacks] = useState<File[]>([])
  // frontIndex -> backIndex (absent = no back)
  const [assignments, setAssignments] = useState<Record<number, number>>({})
  const [cropAll, setCropAll] = useState(false)
  const [cropPerFront, setCropPerFront] = useState<Record<number, boolean>>({})
  const [frontsOnly, setFrontsOnly] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // T-03: multiselection state (sorted list of selected front indices).
  const [multiselect, setMultiselect] = useState(false)
  const [selected, setSelected] = useState<number[]>([])

  const frontPreviews = useMemo(
    () => fronts.map((f) => URL.createObjectURL(f)),
    [fronts]
  )
  const backPreviews = useMemo(
    () => backs.map((b) => URL.createObjectURL(b)),
    [backs]
  )

  const onFrontsFiles = (files: File[]) => {
    setFronts(files)
    setAssignments({})
    setCropPerFront({})
    setSelected([])
  }

  const onBacksFiles = (files: File[]) => {
    setBacks(files)
    setAssignments({})
  }

  // ---- T-03: multiselection helpers -------------------------------------

  const isSelected = (frontIdx: number) => selected.includes(frontIdx)

  const toggleSelected = (frontIdx: number) => {
    setSelected((prev) =>
      prev.includes(frontIdx)
        ? prev.filter((i) => i !== frontIdx)
        : [...prev, frontIdx].sort((a, b) => a - b)
    )
  }

  const toggleSelectAll = () => {
    setSelected((prev) =>
      prev.length === fronts.length ? [] : fronts.map((_, i) => i)
    )
  }

  /** Indices the next batch operation will affect: the selection, or the
   *  single row when nothing is selected (same rule as the desktop GUI). */
  const targetIndices = (frontIdx: number): number[] =>
    multiselect && selected.includes(frontIdx) ? selected : [frontIdx]

  const setBackForTargets = (frontIdx: number, value: string) => {
    const targets = targetIndices(frontIdx)
    setAssignments((prev) => {
      const next = { ...prev }
      targets.forEach((idx) => {
        if (value === '') delete next[idx]
        else next[idx] = Number(value)
      })
      return next
    })
  }

  const toggleCropForTargets = (frontIdx: number) => {
    const targets = targetIndices(frontIdx)
    setCropPerFront((prev) => {
      const next = { ...prev }
      // Batch semantics: if ANY target lacks the crop flag, turn it on for all
      // (so repeated clicks alternate off/on for the whole selection).
      const turnOn = targets.some((idx) => !next[idx])
      targets.forEach((idx) => {
        next[idx] = turnOn
      })
      return next
    })
  }

  /** Remove the given front indices, reindexing assignments/crop maps. */
  const removeFronts = (targets: number[]) => {
    const dropped = new Set(targets)
    const shiftFor = (idx: number) =>
      targets.filter((t) => t < idx).length
    setFronts((prev) => prev.filter((_, i) => !dropped.has(i)))
    setAssignments((prev) => {
      const next: Record<number, number> = {}
      Object.entries(prev).forEach(([k, v]) => {
        const idx = Number(k)
        if (!dropped.has(idx)) next[idx - shiftFor(idx)] = v
      })
      return next
    })
    setCropPerFront((prev) => {
      const next: Record<number, boolean> = {}
      Object.entries(prev).forEach(([k, v]) => {
        const idx = Number(k)
        if (!dropped.has(idx)) next[idx - shiftFor(idx)] = v
      })
      return next
    })
    setSelected([])
  }

  const removeTargets = (frontIdx: number) => {
    removeFronts(targetIndices(frontIdx))
  }

  const handleSubmit = async () => {
    setError(null)
    if (fronts.length === 0) {
      setError('Sube al menos una imagen frontal.')
      return
    }
    setSubmitting(true)
    try {
      const crop: 'all' | 'none' | boolean[] = cropAll
        ? 'all'
        : fronts.map((_, i) => Boolean(cropPerFront[i]))
      const job = await jobsApi.createLocal(
        fronts,
        backs,
        assignments,
        crop,
        'mtg',
        frontsOnly
      )
      navigate(`/job/${job.id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al crear el trabajo')
      setSubmitting(false)
    }
  }

  const hasSelection = selected.length > 0
  const allSelected = selected.length === fronts.length && fronts.length > 0

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="space-y-2">
        <h1 className="text-3xl font-bold text-gray-900">Subir imágenes locales</h1>
        <p className="text-gray-600">
          Usa tus propias imágenes de cartas (frontales y reversos personalizados)
          sin buscar en Scryfall. Opcionalmente aplica el recorte de borde MPC.
        </p>
      </div>

      {/* Fronts */}
      <section className="bg-white border border-gray-200 rounded-lg p-6 space-y-3">
        <h2 className="text-lg font-semibold">1. Frontales ({fronts.length})</h2>
        <DropZone
          onFiles={onFrontsFiles}
          accept="image/*"
          multiple
          icon="🖼️"
          label="Arrastra las imágenes frontales"
          hint="Una imagen por carta, en orden"
        />
      </section>

      {/* Backs */}
      <section className="bg-white border border-gray-200 rounded-lg p-6 space-y-3">
        <h2 className="text-lg font-semibold">2. Reversos personalizados ({backs.length})</h2>
        <p className="text-sm text-gray-500">
          Opcional. Sube uno o varios reversos; luego asígnalos a cada frontal.
        </p>
        <DropZone
          onFiles={onBacksFiles}
          accept="image/*"
          multiple
          icon="🔄"
          label="Arrastra los reversos"
          hint="Opcional: uno o varios reversos para asignar"
        />
        {backs.length > 0 && (
          <div className="flex flex-wrap gap-3 pt-2">
            {backs.map((b, i) => (
              <div key={i} className="text-center">
                <img
                  src={backPreviews[i]}
                  alt={b.name}
                  className="w-16 h-24 object-cover rounded border border-gray-300"
                />
                <div className="text-xs text-gray-500 mt-1">Reverso {i}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Options */}
      <section className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
        <h2 className="text-lg font-semibold">3. Opciones</h2>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={cropAll}
            onChange={(e) => setCropAll(e.target.checked)}
          />
          Recortar borde MPC en todas las imágenes
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={frontsOnly}
            onChange={(e) => setFrontsOnly(e.target.checked)}
          />
          Solo frontales (no generar página de reversos)
        </label>
      </section>

      {/* Per-card assignment */}
      {fronts.length > 0 && (
        <section className="bg-white border border-gray-200 rounded-lg p-6 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-lg font-semibold">4. Asignación por carta</h2>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={multiselect}
                onChange={(e) => {
                  setMultiselect(e.target.checked)
                  if (!e.target.checked) setSelected([])
                }}
              />
              Multiselección
            </label>
          </div>

          {/* T-03: batch operations bar (like the desktop GUI's multiselect). */}
          {multiselect && (
            <div className="flex items-center gap-2 flex-wrap text-sm bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
              <span className="text-gray-700">
                {hasSelection
                  ? `${selected.length} seleccionada${selected.length === 1 ? '' : 's'}`
                  : 'Ninguna seleccionada'}
              </span>
              <Button size="sm" variant="secondary" onClick={toggleSelectAll}>
                {allSelected ? 'Deseleccionar todas' : 'Seleccionar todas'}
              </Button>
              {backs.length > 0 && !frontsOnly && (
                <>
                  {backs.map((_, bi) => (
                    <Button
                      key={bi}
                      size="sm"
                      variant="secondary"
                      disabled={!hasSelection}
                      onClick={() => {
                        setAssignments((prev) => {
                          const next = { ...prev }
                          selected.forEach((idx) => {
                            next[idx] = bi
                          })
                          return next
                        })
                        setSelected([])
                      }}
                    >
                      → Reverso {bi}
                    </Button>
                  ))}
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!hasSelection}
                    onClick={() => {
                      setAssignments((prev) => {
                        const next = { ...prev }
                        selected.forEach((idx) => {
                          delete next[idx]
                        })
                        return next
                      })
                      setSelected([])
                    }}
                  >
                    Sin reverso
                  </Button>
                </>
              )}
              <Button
                size="sm"
                variant="secondary"
                disabled={!hasSelection}
                onClick={() => {
                  setCropPerFront((prev) => {
                    const next = { ...prev }
                    const turnOn = selected.some((idx) => !next[idx])
                    selected.forEach((idx) => {
                      next[idx] = turnOn
                    })
                    return next
                  })
                  setSelected([])
                }}
              >
                Recortar borde
              </Button>
              <Button
                size="sm"
                variant="danger"
                disabled={!hasSelection}
                onClick={() => removeFronts(selected)}
              >
                Eliminar seleccionadas
              </Button>
              {hasSelection && (
                <Button size="sm" variant="secondary" onClick={() => setSelected([])}>
                  Limpiar selección
                </Button>
              )}
            </div>
          )}

          <div className="space-y-3">
            {fronts.map((f, i) => (
              <div
                key={i}
                className={`flex items-center gap-4 border-b border-gray-100 pb-3 last:border-0 ${
                  multiselect && isSelected(i) ? 'bg-blue-50 -mx-2 px-2 rounded' : ''
                }`}
              >
                {multiselect && (
                  <input
                    type="checkbox"
                    aria-label={`Seleccionar ${f.name}`}
                    checked={isSelected(i)}
                    onChange={() => toggleSelected(i)}
                  />
                )}
                <img
                  src={frontPreviews[i]}
                  alt={f.name}
                  className="w-14 h-20 object-cover rounded border border-gray-300"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-800 truncate">{f.name}</div>
                  {!cropAll && (
                    <label className="flex items-center gap-2 text-xs text-gray-600 mt-1">
                      <input
                        type="checkbox"
                        checked={Boolean(cropPerFront[i])}
                        onChange={() => toggleCropForTargets(i)}
                      />
                      Recortar borde MPC
                    </label>
                  )}
                </div>
                {backs.length > 0 && !frontsOnly && (
                  <select
                    value={assignments[i] ?? ''}
                    onChange={(e) => setBackForTargets(i, e.target.value)}
                    className="text-sm border border-gray-300 rounded-lg px-2 py-1.5"
                  >
                    <option value="">Sin reverso</option>
                    {backs.map((_, bi) => (
                      <option key={bi} value={bi}>
                        Reverso {bi}
                      </option>
                    ))}
                  </select>
                )}
                {multiselect && (
                  <button
                    type="button"
                    aria-label={`Eliminar ${f.name}`}
                    onClick={() => removeTargets(i)}
                    className="text-gray-400 hover:text-red-600 text-sm px-1"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
          {error}
        </div>
      )}

      <div className="flex justify-end">
        <Button size="lg" onClick={handleSubmit} disabled={submitting || fronts.length === 0}>
          {submitting ? 'Creando…' : 'Generar PDF'}
        </Button>
      </div>
    </div>
  )
}
